/**
 * Smooth Scrolling with Lenis
 * Provides smooth scrolling experience and integrates seamlessly with GSAP ScrollTrigger
 * Respects prefers-reduced-motion and works on both mobile and desktop
 */

"use strict";

let lenis = null;
let isInitialized = false;

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion() {
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Initialize Lenis smooth scrolling
 */
function initSmoothScroll() {
  // Only initialize on desktop (width > 999px)
  const isMobile = window.innerWidth <= 999;
  if (isMobile) {
    return;
  }

  // Don't initialize if already done or user prefers reduced motion
  if (isInitialized || prefersReducedMotion()) {
    return;
  }

  // Check if Lenis is available
  if (typeof Lenis === "undefined") {
    // Retry after a short delay if Lenis hasn't loaded yet
    setTimeout(initSmoothScroll, 100);
    return;
  }

  // Initialize Lenis with optimal settings for desktop
  lenis = new Lenis({
    duration: 1.2, // Smooth scroll duration
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Custom easing for smooth feel
    orientation: "vertical", // Vertical scrolling
    gestureOrientation: "vertical",
    smoothWheel: true, // Enable smooth scrolling for mouse wheel
    wheelMultiplier: 1, // Scroll speed multiplier
    smoothTouch: false, // Disable smooth scrolling on touch (desktop only anyway)
    touchMultiplier: 2,
    infinite: false, // Don't create infinite scroll
  });

  // Sync Lenis with GSAP ticker (always) and ScrollTrigger (when available)
  function syncWithGSAP() {
    if (typeof gsap !== "undefined" && lenis) {
      // Integrate Lenis with GSAP's animation ticker (works on all devices)
      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });

      // Disable GSAP's default lag smoothing for better performance with Lenis
      gsap.ticker.lagSmoothing(0);

      // Sync with ScrollTrigger if available (desktop only)
      if (typeof ScrollTrigger !== "undefined") {
        // Sync Lenis scroll events with ScrollTrigger
        lenis.on("scroll", ScrollTrigger.update);
        // Refresh ScrollTrigger after Lenis is initialized
        ScrollTrigger.refresh();
      }

      return true;
    }
    return false;
  }

  // Try to sync immediately if GSAP is already loaded
  if (syncWithGSAP()) {
    // Already synced
  } else {
    // Wait for GSAP to load (loaded on all devices)
    let checkCount = 0;
    const maxChecks = 50; // 5 seconds max (50 * 100ms)

    const checkGSAP = setInterval(() => {
      checkCount++;
      if (syncWithGSAP() || checkCount >= maxChecks) {
        clearInterval(checkGSAP);
      }
    }, 100);

    // Also listen for when ScrollTrigger might be dynamically loaded (desktop)
    window.addEventListener("scrollTriggerReady", () => {
      syncWithGSAP();
    });
  }

  // Make Lenis instance globally available for programmatic control if needed
  window.lenis = lenis;

  isInitialized = true;

  // Dispatch event for other scripts to listen to
  document.dispatchEvent(
    new CustomEvent("lenisInitialized", { detail: { lenis } })
  );
}

/**
 * Get scroll position (works with or without Lenis)
 */
function getScrollPosition() {
  if (lenis) {
    return lenis.scroll;
  }
  return window.pageYOffset || document.documentElement.scrollTop;
}

/**
 * Scroll to position (works with or without Lenis)
 */
function scrollTo(target, options = {}) {
  if (lenis) {
    lenis.scrollTo(target, options);
  } else {
    if (typeof target === "number") {
      window.scrollTo({
        top: target,
        behavior: options.immediate ? "auto" : "smooth",
      });
    } else if (typeof target === "string" || target instanceof Element) {
      const element =
        typeof target === "string" ? document.querySelector(target) : target;
      if (element) {
        element.scrollIntoView({
          behavior: options.immediate ? "auto" : "smooth",
          block: options.offset ? "start" : "center",
        });
      }
    }
  }
}

/**
 * Stop smooth scrolling (useful for programmatic navigation)
 */
function stopScroll() {
  if (lenis) {
    lenis.stop();
  }
}

/**
 * Resume smooth scrolling
 */
function startScroll() {
  if (lenis) {
    lenis.start();
  }
}

/**
 * Destroy Lenis instance (cleanup)
 */
function destroySmoothScroll() {
  if (lenis) {
    lenis.destroy();
    lenis = null;
    isInitialized = false;
    window.lenis = null;
  }
}

// Make functions globally available
window.getScrollPosition = getScrollPosition;
window.scrollToPosition = scrollTo;
window.stopSmoothScroll = stopScroll;
window.startSmoothScroll = startScroll;
window.destroySmoothScroll = destroySmoothScroll;

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", initSmoothScroll);

// Reinitialize on pageshow (for back/forward navigation)
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    // Page was restored from cache
    isInitialized = false;
    if (lenis) {
      destroySmoothScroll();
    }
    setTimeout(initSmoothScroll, 100);
  }
});

// Handle resize events (Lenis handles this internally, but we can refresh ScrollTrigger)
// Also handle switching between mobile/desktop
let resizeTimeout;
window.addEventListener(
  "resize",
  () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 999;

      // If switched to mobile, destroy Lenis
      if (isMobile && lenis && isInitialized) {
        destroySmoothScroll();
      }
      // If switched to desktop, initialize Lenis
      else if (!isMobile && !isInitialized && !prefersReducedMotion()) {
        initSmoothScroll();
      }

      // Refresh ScrollTrigger if available
      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.refresh();
      }
    }, 250);
  },
  { passive: true }
);
